import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    VStack,
    HStack,
    Flex,
    Icon,
    Heading,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    Link as ChakraLink,
} from '@chakra-ui/react';
import { FaClipboardList } from 'react-icons/fa';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { FiCheckCircle, FiExternalLink } from 'react-icons/fi';
import { ORDER_ACTION_ICONS } from '../../utils/orderActionIcons';
import React, { useContext } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ReadyToOrderEntry } from '../../hooks/useGetOrdersFeed';
import ActionBadge from '../ActionBadge';
import { formatPrice, formatPercentage } from '../../functions';
import { useArchiveOrder } from '../../hooks/useArchiveOrder';
import { useCompleteOrder } from '../../hooks/useCompleteOrder';
import SortableTableHeader, { SortState, DashboardSortField } from './components/SortableTableHeader';
import SessionContext from '../../contexts/SessionContext';
import { Vin, NoteCountBadge, SupplierCommunicationCountBadge, EnterEstimateBadge } from './components';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import ResizableTh from '../ResizableTh';
import { STICKY_TABLE_HEADER_Z_INDEX } from '../../utils/tableCopySx';
import {
    DashboardRoColumnRow,
    DashboardRoSelectAllHeader,
    type DashboardBulkSelection,
    getDashboardBulkTableSx,
    getDashboardRoColumnPl,
    getDashboardRowHighlight,
    handleOrderBulkRowClick,
} from './dashboardBulkSelection';

interface SelectionTableProps {
    orders: Array<ReadyToOrderEntry>;
    sortState?: SortState;
    onSortChange?: (field: DashboardSortField) => void;
    bulkSelection?: DashboardBulkSelection;
}

const SelectionTable = ({ orders, sortState, onSortChange, bulkSelection }: SelectionTableProps) => {
    const navigate = useNavigate();
    const archiveOrder = useArchiveOrder();
    const { widths: colWidths, updateWidth: updateColWidth } = useColumnWidths('dashboard_ready-to-order');
    const completeOrder = useCompleteOrder();

    const sessionData = useContext(SessionContext);
    const canComplete = !!sessionData?.isAuthenticated && sessionData.permissions.includes('complete-uncomplete');
    const canArchive = !!sessionData?.isAuthenticated && sessionData.permissions.includes('archive-unarchive');

    const handleArchiveOrder = (orderId: string) => {
        archiveOrder.mutate({ orderId });
    };

    const handleCompleteOrder = (orderId: string) => {
        completeOrder.mutate({ orderId });
    };

    const defaultSortState: SortState = { field: null, direction: 'asc' };
    const currentSortState = sortState || defaultSortState;

    if (!orders || orders.length === 0) {
        return (
            <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
                px={6}
                textAlign="center"
            >
                <Icon
                    as={FaClipboardList as React.ElementType}
                    w={16}
                    h={16}
                    color="gray.300"
                    mb={6}
                />
                <Heading
                    size="lg"
                    color="gray.600"
                    mb={2}
                >
                    No orders ready to order
                </Heading>
                <Text
                    color="gray.500"
                    fontSize="md"
                    maxW="md"
                >
                    Orders ready for ordering will appear here
                </Text>
            </Flex>
        );
    }

    return (
        <>
            <Table variant="simple" size='sm' minW="100%" sx={getDashboardBulkTableSx(bulkSelection, Object.keys(colWidths).length > 0 ? { tableLayout: 'fixed', '& td': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } : undefined)}>
                <Thead position="sticky" top={0} zIndex={STICKY_TABLE_HEADER_Z_INDEX}>
                    <Tr bg="gray.50">
                        <ResizableTh columnKey="roNumber" storedWidth={colWidths.roNumber} onResize={updateColWidth} py={2.5} pl={getDashboardRoColumnPl(bulkSelection)} pr={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="100px" position="relative">
                            <DashboardRoSelectAllHeader bulkSelection={bulkSelection} />
                            RO Number
                        </ResizableTh>
                        <ResizableTh columnKey="vehicle" storedWidth={colWidths.vehicle} onResize={updateColWidth} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="190px">Vehicle</ResizableTh>
                        <ResizableTh columnKey="customer" storedWidth={colWidths.customer} onResize={updateColWidth} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="120px">Customer</ResizableTh>
                        <ResizableTh columnKey="location" storedWidth={colWidths.location} onResize={updateColWidth} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="140px">Location</ResizableTh>
                        <ResizableTh columnKey="partsOrdered" storedWidth={colWidths.partsOrdered} onResize={updateColWidth} py={2.5} px={3} borderBottomWidth="1px" borderColor="gray.200" minW="100px">
                            {onSortChange ? (
                                <SortableTableHeader
                                    label="PARTS ORDERED"
                                    field="partsOrdered"
                                    sortState={currentSortState}
                                    onSort={onSortChange}
                                />
                            ) : (
                                <Text color="gray.500" fontSize="11px" fontWeight="semibold" textAlign="center">
                                    Parts Ordered
                                </Text>
                            )}
                        </ResizableTh>
                        <ResizableTh columnKey="daysActive" storedWidth={colWidths.daysActive} onResize={updateColWidth} py={2.5} px={3} borderBottomWidth="1px" borderColor="gray.200" minW="110px">
                            {onSortChange ? (
                                <SortableTableHeader
                                    label="DAYS ACTIVE"
                                    field="daysActive"
                                    sortState={currentSortState}
                                    onSort={onSortChange}
                                />
                            ) : (
                                <Text color="gray.500" fontSize="11px" fontWeight="semibold" textAlign="center">
                                    Days Active
                                </Text>
                            )}
                        </ResizableTh>
                        <ResizableTh columnKey="orderValue" storedWidth={colWidths.orderValue} onResize={updateColWidth} py={2.5} px={3} borderBottomWidth="1px" borderColor="gray.200" minW="120px">
                            {onSortChange ? (
                                <SortableTableHeader
                                    label="ORDER VALUE"
                                    field="orderValue"
                                    sortState={currentSortState}
                                    onSort={onSortChange}
                                />
                            ) : (
                                <Text color="gray.500" fontSize="11px" fontWeight="semibold" textAlign="center">
                                    Order Value
                                </Text>
                            )}
                        </ResizableTh>
                        <ResizableTh columnKey="grossMargin" storedWidth={colWidths.grossMargin} onResize={updateColWidth} py={2.5} px={3} borderBottomWidth="1px" borderColor="gray.200" minW="140px">
                            {onSortChange ? (
                                <SortableTableHeader
                                    label="GROSS MARGIN"
                                    field="grossMargin"
                                    sortState={currentSortState}
                                    onSort={onSortChange}
                                />
                            ) : (
                                <Text color="gray.500" fontSize="11px" fontWeight="semibold" textAlign="center">
                                    Gross Margin
                                </Text>
                            )}
                        </ResizableTh>
                        <ResizableTh columnKey="deliveryTime" storedWidth={colWidths.deliveryTime} onResize={updateColWidth} py={2.5} px={3} borderBottomWidth="1px" borderColor="gray.200" minW="120px">
                            {onSortChange ? (
                                <SortableTableHeader
                                    label="DELIVERY TIME"
                                    field="deliveryTime"
                                    sortState={currentSortState}
                                    onSort={onSortChange}
                                    justify="flex-start"
                                />
                            ) : (
                                <Text color="gray.500" fontSize="11px" fontWeight="semibold" textAlign="left">
                                    Delivery Time
                                </Text>
                            )}
                        </ResizableTh>
                        <Th w="200px" py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Status</Th>
                        {canArchive && <Th py={2.5} pr={6} pl={1} textAlign="right" color="gray.500" fontSize="11px" fontWeight="semibold" borderBottomWidth="1px" borderColor="gray.200" w="60px" minW="60px"></Th>}
                    </Tr>
                </Thead>
                <Tbody>
                    {orders.map((order, rowIndex) => {
                        const isSelected = bulkSelection?.selectedOrderIds.has(order._id) ?? false;

                        return (
                            <Tr
                                key={order._id}
                                {...getDashboardRowHighlight(isSelected, false)}
                                onClick={(e) => {
                                    handleOrderBulkRowClick(
                                        e,
                                        order._id,
                                        rowIndex,
                                        (id, idx, shift) => bulkSelection?.onToggle(id, idx, shift),
                                        () => navigate(`/order/${order._id}`, { state: { source: 'dashboard', initialStatus: 'selection' } }),
                                        !!bulkSelection?.enabled,
                                    );
                                }}
                            >
                                <Td
                                    py={3}
                                    pl={getDashboardRoColumnPl(bulkSelection)}
                                    pr={3}
                                    position={bulkSelection?.enabled ? 'relative' : undefined}
                                    whiteSpace="normal"
                                    wordBreak="break-word"
                                    borderBottomWidth="1px"
                                    borderColor="gray.100"
                                >
                                    <DashboardRoColumnRow
                                        bulkSelection={bulkSelection}
                                        orderId={order._id}
                                        rowIndex={rowIndex}
                                        isChecked={isSelected}
                                    >
                                        <EnterEstimateBadge canEnter={order.canEnterEstimate} />
                                        <ChakraLink
                                            as={RouterLink}
                                            to={`/order/${order._id}`}
                                            state={{ source: 'dashboard', initialStatus: 'selection' }}
                                            onClick={(e) => e.stopPropagation()}
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color="gray.800"
                                            _hover={{ textDecoration: 'underline' }}
                                        >
                                            {order.roNumber}
                                        </ChakraLink>
                                        <NoteCountBadge noteCount={order.noteCount} orderId={order._id} />
                                        <SupplierCommunicationCountBadge count={order.supplierCommunicationCount} />
                                    </DashboardRoColumnRow>
                                </Td>
                                <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" borderBottomWidth="1px" borderColor="gray.100">
                                    <VStack align="start" spacing={0.5}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                            {order.ymm}
                                        </Text>
                                        <Vin vin={order.vin} />
                                    </VStack>
                                </Td>
                                <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" minW="120px" borderBottomWidth="1px" borderColor="gray.100">
                                    {order.lastName && order.firstName ?
                                        <VStack align="start" spacing={0}>
                                            <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                                {order.lastName || '—'}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {order.firstName || '—'}
                                            </Text>
                                        </VStack> :
                                        <Text fontSize="sm" color="gray.500">
                                            —
                                        </Text>
                                    }
                                </Td>
                                <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" minW="140px" borderBottomWidth="1px" borderColor="gray.100">
                                    <Text fontSize="sm" color="gray.700">
                                        {order.locationName || '—'}
                                    </Text>
                                </Td>
                                <Td py={3} px={3} whiteSpace="normal" textAlign="center" minW="140px" borderBottomWidth="1px" borderColor="gray.100">
                                    <Text fontSize="sm" color="gray.700" fontWeight='medium' textAlign="center">
                                        {order.partsOrdered}/{order.totalParts}
                                    </Text>
                                </Td>
                                <Td py={3} px={3} whiteSpace="normal" textAlign="center" minW="110px" borderBottomWidth="1px" borderColor="gray.100">
                                    <Text fontSize="sm" color="gray.700" fontWeight='medium' textAlign="center">
                                        {order.daysOpen}
                                    </Text>
                                </Td>
                                <Td py={3} px={3} whiteSpace="normal" textAlign="center" borderBottomWidth="1px" borderColor="gray.100">
                                    <Text fontSize="sm" color="gray.700" fontWeight='medium' textAlign="center">
                                        {formatPrice(order.orderValue)}
                                    </Text>
                                </Td>
                                <Td py={3} px={3} whiteSpace="normal" textAlign="center" borderBottomWidth="1px" borderColor="gray.100">
                                    <Text fontSize="sm" color="gray.700" fontWeight='medium' textAlign="center">
                                        {formatPercentage(order.grossProfitMargin)}
                                    </Text>
                                </Td>
                                <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" borderBottomWidth="1px" borderColor="gray.100">
                                    <Text fontSize="sm" color="gray.700">
                                        {order.deliveryTime}
                                    </Text>
                                </Td>
                                <Td py={3} px={3} whiteSpace="nowrap" textAlign="left" borderBottomWidth="1px" borderColor="gray.100">
                                    <VStack align="flex-start" spacing={1} flexWrap="nowrap">
                                        {order.status.map(s => (
                                            <ActionBadge
                                                action={s.key}
                                                orderId={order._id}
                                                key={s.key}
                                                total={s.total}
                                                source='dashboard'
                                            />
                                        ))}
                                    </VStack>
                                </Td>
                                {canArchive && <Td py={3} pr={6} pl={1} textAlign="right" w="60px" minW="60px" borderBottomWidth="1px" borderColor="gray.100">
                                    <Menu>
                                        <MenuButton
                                            as={IconButton}
                                            aria-label="Options"
                                            icon={<Icon as={HiOutlineDotsVertical as React.ElementType} w={4} h={4} />}
                                            size="xs"
                                            variant="outline"
                                            colorScheme="gray"
                                            borderRadius="lg"
                                            borderColor="gray.300"
                                            _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
                                            _active={{ bg: 'gray.100', borderColor: 'gray.400' }}
                                            _focus={{ boxShadow: 'outline' }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <MenuList py={1} minW="200px">
                                            <MenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(`/order/${order._id}`, '_blank');
                                                }}
                                                fontSize="sm"
                                                px={3}
                                                py={2}
                                                color="gray.700"
                                                _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={FiExternalLink as React.ElementType} boxSize={4} color="gray.500" />
                                                    <Text>Open in New Tab</Text>
                                                </HStack>
                                            </MenuItem>
                                            {canComplete && order.canComplete && <MenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCompleteOrder(order._id);
                                                }}
                                                fontSize="sm"
                                                px={3}
                                                py={2}
                                                color="gray.700"
                                                _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={FiCheckCircle as React.ElementType} boxSize={4} color="gray.500" />
                                                    <Text>Mark Complete</Text>
                                                </HStack>
                                            </MenuItem>}
                                            <MenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleArchiveOrder(order._id);
                                                }}
                                                fontSize="sm"
                                                px={3}
                                                py={2}
                                                color="gray.700"
                                                _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={ORDER_ACTION_ICONS.archive as React.ElementType} boxSize={4} color="gray.500" />
                                                    <Text>Archive Order</Text>
                                                </HStack>
                                            </MenuItem>
                                        </MenuList>
                                    </Menu>
                                </Td>}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
        </>
    );
};

export default SelectionTable;
